// External modules
import acHTTPClient from 'services/ACHTTPClient';
import config from '../../../config/config';
import { useQuery } from 'react-query';
import { isEmpty } from 'lodash';


// Internal modules
import * as constants from '../constants';

const productSearchAPIURL = `${config.productSearchAPIURL}`;

// Route product discovery reads content-api, which authors products, so drafts
// and never-published products are selectable. The legacy redux thunks below
// still read the published product-search index.
const contentAPIV1Url = `${config.contentAPIV1Url}`;

/**
 * Trims a content-api product down to what a route needs. The route document
 * stores this snapshot, and ac-experience re-decorates it on save — a full
 * product payload is large enough to be rejected by the API.
 */
const toRouteProduct = (product = {}) => {
  const productCities = (product.productCities || []).map((pc) => ({
    id: pc.id,
    name: pc.name,
    city: pc.city,
    state: pc.state,
    country: pc.country,
    timeZoneId: pc.timeZoneId,
  }));

  // A product's own prices are often empty because the legacy website inherits
  // them from an ancestor group; the merged presentationProduct carries them.
  const prices = !isEmpty(product.prices)
    ? product.prices
    : product.presentationProduct?.prices || [];

  return {
    id: product.id,
    name: product.name,
    shortName: product.shortName,
    status: product.status,
    type: product.type,
    category: product.category,
    prices,
    productCities,
  };
};

const getProductByIdErrored = error => {
  return {
    type: constants.GET_PRODUCT_BY_ID_ERROR,
    error,
  };
};

const getProductsErrored = error => {
  return {
    type: constants.GET_PRODUCTS_ERROR,
    error,
  };
};

const getProductByIdSuccess = product => {
  return {
    type: constants.GET_PRODUCT_BY_ID_SUCCESS,
    product: product,
  };
};

const getProductsSuccess = products => {
  return {
    type: constants.GET_PRODUCTS_SUCCESS,
    products: products,
  };
};

const getProductById = id => {
  let apiURL = `${productSearchAPIURL}/api/v1/products?id=${id}`;
  return dispatch => {
    const config = { url: apiURL, method: 'GET' };
    acHTTPClient
      .request(config)
      .then(response => {
        if (!response.data || response.data.error) {
          dispatch(getProductByIdErrored(response.statusText));
        }
        dispatch(getProductByIdSuccess(response.data.data[0]));
      })
      .catch(error => {
        dispatch(getProductByIdErrored(error));
      });
  };
};

const getProductByIdImperative = async id => {
    let apiURL = `${contentAPIV1Url}/products/${id}`;
      const config = { url: apiURL, method: 'GET' };
      return await acHTTPClient
        .request(config)
        .then(response => {
          if (!response.data || response.data.error || !response.data?.data) {
            alert('failed to fetch product ' + id);
            return {}
          }
          return toRouteProduct(response.data.data);
        })
        .catch(error => {
            alert('failed to fetch product ' + id);
            return {}
        });
    };

const getProductsByQuery = query => {
  let apiURL = `${productSearchAPIURL}/api/v1/products/search-collection?query=${query}&projections=location`;
  return dispatch => {
    const config = { url: apiURL, method: 'GET' };
    acHTTPClient
      .request(config)
      .then(response => {
        if (!response.data || response.data.error || response.data.data.error) {
          dispatch(getProductsErrored(response.statusText));
        }
        dispatch(getProductsSuccess(response.data.data));
      })
      .catch(error => {
        dispatch(getProductsErrored(error));
      });
  };
};

const getProducts = () => {
  let apiURL = `${productSearchAPIURL}/api/v1/products/presentation`;
  return dispatch => {
    const config = { url: apiURL, method: 'GET' };
    acHTTPClient
      .request(config)
      .then(response => {
        if (!response.data || response.data.error) {
          dispatch(getProductsErrored(response.statusText));
        }
        dispatch(getProductsSuccess(response.data));
      })
      .catch(error => {
        dispatch(getProductsErrored(error));
      });
  };
};

const useFetcProductById = (id) => {
    console.log('useGetProductById', id);
    const query = useQuery(['productById', id], () =>
        fetch(`${productSearchAPIURL}/api/v1/products?id=${id}`)
            .then(async (res) => {
                console.log({ res })
                const data = await res.json() || {}
                return data.data || {};
            }, { placeHolderData: {} })
    );
    return query;
}

/**
 * content-api matches a search term against product id and name and returns
 * every hit unranked, so rank them here: whole-word/prefix name matches first,
 * then any other name match, then id-only matches.
 */
const rankProductsByRelevance = (products, searchTerm) => {
    const term = (searchTerm || '').trim().toLowerCase();
    if (!term) return products;

    const score = (product) => {
        const name = (product.name || '').toLowerCase();
        if (name.startsWith(term)) return 0;
        if (name.includes(` ${term}`)) return 1;
        if (name.includes(term)) return 2;
        return 3;
    };

    return [...products].sort((a, b) => score(a) - score(b) || (a.name || '').localeCompare(b.name || ''));
}

const useFetchProductsByName = (searchTerm) => {
    const query = useQuery(['productQuerySearch', searchTerm], () =>
        fetch(`${contentAPIV1Url}/products/browse?searchQuery=${encodeURIComponent(searchTerm || '')}`)
            .then(async (res) => {
                const data = await res.json() || {}
                return rankProductsByRelevance(data.data || [], searchTerm);
            }, { placeHolderData: [] })
    );
    return query;
}

export { getProductById, getProductsByQuery, getProducts, useFetcProductById, useFetchProductsByName, getProductByIdImperative };


